import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { API } from "../App";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar
} from "recharts";
import { Calendar, TrendingUp, TrendingDown, Activity } from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const AnalyticsTrend = ({ preventivi, sessionId, token }) => {
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrend = async () => {
      if (!sessionId) return;
      
      try {
        const response = await axios.get(`${API}/analytics/trend?session_id=${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTrendData(response.data);
      } catch (error) {
        console.error("Error fetching trend:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTrend();
  }, [sessionId, token]);

  // Process data for charts
  const chartData = useMemo(() => {
    return trendData.map(d => ({
      ...d,
      dateLabel: d.date ? new Date(d.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) : "N/A",
      valoreMila: d.value / 1000
    }));
  }, [trendData]);

  // Calculate trend metrics
  const metrics = useMemo(() => {
    if (chartData.length < 2) return null;
    
    const firstHalf = chartData.slice(0, Math.floor(chartData.length / 2));
    const secondHalf = chartData.slice(Math.floor(chartData.length / 2));
    
    const firstHalfTotal = firstHalf.reduce((sum, d) => sum + d.count, 0);
    const secondHalfTotal = secondHalf.reduce((sum, d) => sum + d.count, 0);
    
    const firstHalfValue = firstHalf.reduce((sum, d) => sum + d.value, 0);
    const secondHalfValue = secondHalf.reduce((sum, d) => sum + d.value, 0);
    
    const countTrend = firstHalfTotal > 0 ? ((secondHalfTotal - firstHalfTotal) / firstHalfTotal * 100).toFixed(1) : 0;
    const valueTrend = firstHalfValue > 0 ? ((secondHalfValue - firstHalfValue) / firstHalfValue * 100).toFixed(1) : 0;
    
    const avgPerDay = chartData.length > 0 ? 
      chartData.reduce((sum, d) => sum + d.count, 0) / chartData.length : 0;
    const avgValuePerDay = chartData.length > 0 ? 
      chartData.reduce((sum, d) => sum + d.value, 0) / chartData.length : 0;
    
    return {
      countTrend: parseFloat(countTrend),
      valueTrend: parseFloat(valueTrend),
      avgPerDay: avgPerDay.toFixed(1),
      avgValuePerDay,
      totalDays: chartData.length,
      peakDay: chartData.reduce((max, d) => d.count > (max?.count || 0) ? d : max, null),
      peakValueDay: chartData.reduce((max, d) => d.value > (max?.value || 0) ? d : max, null)
    };
  }, [chartData]);

  // Weekly aggregation
  const weeklyData = useMemo(() => {
    const weeks = {};
    chartData.forEach(d => {
      if (!d.date) return;
      const date = new Date(d.date);
      const weekNum = Math.ceil((date.getDate()) / 7);
      const month = date.toLocaleDateString("it-IT", { month: "short" });
      const key = `${month} S${weekNum}`;
      
      if (!weeks[key]) {
        weeks[key] = { week: key, count: 0, value: 0 };
      }
      weeks[key].count += d.count;
      weeks[key].value += d.value;
    });
    
    return Object.values(weeks);
  }, [chartData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        Nessun dato di trend disponibile
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-trend-page">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
          Trend Temporali
        </h2>
        <Badge variant="outline" className="text-slate-600">
          {metrics?.totalDays || 0} Giorni Analizzati
        </Badge>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${metrics.countTrend >= 0 ? "bg-green-100" : "bg-red-100"}`}>
                  {metrics.countTrend >= 0 ? 
                    <TrendingUp className="h-5 w-5 text-green-600" /> : 
                    <TrendingDown className="h-5 w-5 text-red-600" />
                  }
                </div>
                <div>
                  <p className="text-sm text-slate-500">Trend Quantità</p>
                  <p className={`text-xl font-bold font-heading ${metrics.countTrend >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {metrics.countTrend >= 0 ? "+" : ""}{metrics.countTrend}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${metrics.valueTrend >= 0 ? "bg-green-100" : "bg-red-100"}`}>
                  {metrics.valueTrend >= 0 ? 
                    <TrendingUp className="h-5 w-5 text-green-600" /> : 
                    <TrendingDown className="h-5 w-5 text-red-600" />
                  }
                </div>
                <div>
                  <p className="text-sm text-slate-500">Trend Valore</p>
                  <p className={`text-xl font-bold font-heading ${metrics.valueTrend >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {metrics.valueTrend >= 0 ? "+" : ""}{metrics.valueTrend}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Activity className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Media/Giorno</p>
                  <p className="text-xl font-bold font-heading">{metrics.avgPerDay} prev.</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100">
                  <Calendar className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Giorno Peak</p>
                  <p className="text-lg font-bold font-heading">
                    {metrics.peakDay?.dateLabel || "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Charts */}
      <div className="grid grid-cols-1 gap-6">
        {/* Daily Trend Line Chart */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight">
              Andamento Giornaliero Preventivi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ left: 20, right: 30 }}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0f172a" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0f172a" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [value, "Preventivi"]}
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#0f172a" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorCount)" 
                    name="Preventivi"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Value Trend */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight">
              Andamento Valore Giornaliero
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ left: 20, right: 30 }}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), "Valore"]}
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#f97316" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorValue)" 
                    name="Valore"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Weekly Aggregation */}
        {weeklyData.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg uppercase tracking-tight">
                Aggregazione Settimanale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyData} margin={{ left: 20, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === "value" ? formatCurrency(value) : value,
                        name === "value" ? "Valore" : "Preventivi"
                      ]}
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" fill="#0f172a" name="Preventivi" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="value" fill="#f97316" name="Valore" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Daily Data Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg uppercase tracking-tight">
            Dettaglio Giornaliero
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm" data-testid="trend-detail-table">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Data</th>
                  <th className="text-center py-3 px-4 font-semibold">Preventivi</th>
                  <th className="text-right py-3 px-4 font-semibold">Valore</th>
                  <th className="text-right py-3 px-4 font-semibold">Media €/Prev</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((d, i) => (
                  <tr key={d.date} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium">{d.dateLabel}</td>
                    <td className="py-3 px-4 text-center">{d.count}</td>
                    <td className="py-3 px-4 text-right font-medium">{formatCurrency(d.value)}</td>
                    <td className="py-3 px-4 text-right text-slate-600">
                      {d.count > 0 ? formatCurrency(d.value / d.count) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsTrend;
